import { useEffect, useState } from 'react';
import { Box, Button, LinearProgress, Skeleton, Typography } from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PaymentsIcon from '@mui/icons-material/Payments';
import EventIcon from '@mui/icons-material/Event';
import StarIcon from '@mui/icons-material/Star';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { accountBalanceUrl } from '../../../configurations/api/url';
import { commissionWalletSelector } from '../../../redux/buzzbridge/selectors';
import type { IInfluencer } from '../../../redux/buzzbridge/types';
import { engagementFromInfluencer, totalReachDisplay } from '../utils/creatorProfileHelpers';

const SURFACE_LOW = '#131313';
const SURFACE = '#1a1919';
const SURFACE_HI = '#262626';
const GOLD = '#ffc965';
const MINT = '#00fdc1';
const ON_VARIANT = '#adaaaa';

const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

function fromCents(cents?: number | null): string {
    if (cents == null || Number.isNaN(Number(cents))) return '—';
    return money.format(Number(cents) / 100);
}

interface BalancePayload {
    balance: number;
    currency?: string;
    total_earned: number;
    withdrawable?: boolean;
    payout?: { next_scheduled_payout_at?: string | null };
}

export function CreatorFinanceRail({ influencer, onCashOut }: { influencer: IInfluencer; onCashOut: () => void }) {
    const token = useSelector((s: { authentication: { token?: string } }) => s.authentication.token);
    const commissionWallet = useSelector(commissionWalletSelector);
    const [balance, setBalance] = useState<BalancePayload | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!token) return;
        let cancelled = false;
        setLoading(true);
        axios
            .get<{ data: BalancePayload }>(accountBalanceUrl, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then(res => {
                if (!cancelled) setBalance(res.data?.data ?? null);
            })
            .catch(() => {
                if (!cancelled) setBalance(null);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [token]);

    const reach = totalReachDisplay(influencer);
    const engagement = engagementFromInfluencer(influencer);
    const rating =
        typeof influencer.ratings === 'number' && !Number.isNaN(influencer.ratings) ? influencer.ratings : null;
    const goalPct =
        influencer.monthly_goal_progress_percent != null ? Number(influencer.monthly_goal_progress_percent) : null;
    const monthlyTarget = influencer.monthly_target;
    const monthlyEarned = influencer.monthly_earned_cents;
    const remaining =
        monthlyTarget != null && monthlyTarget > 0 && monthlyEarned != null
            ? Math.max(0, monthlyTarget - monthlyEarned)
            : null;
    const scheduled = balance?.payout?.next_scheduled_payout_at;
    const scheduledLabel = scheduled
        ? new Date(scheduled).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        : null;
    const affiliates = commissionWallet?.commission?.balance_cents;
    const totalEarned =
        balance?.total_earned != null ? money.format(balance.total_earned) : fromCents(influencer.total_earned_cents);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box
                sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    p: 3,
                    borderRadius: 1.5,
                    bgcolor: SURFACE_LOW,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2.5,
                    boxShadow: '0 16px 40px rgba(0,0,0,0.35)',
                }}>
                <Box
                    sx={{
                        position: 'absolute',
                        right: -48,
                        bottom: -48,
                        width: 176,
                        height: 176,
                        borderRadius: '50%',
                        bgcolor: 'rgba(255,201,101,0.1)',
                        filter: 'blur(32px)',
                        pointerEvents: 'none',
                    }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                            sx={{
                                width: 32,
                                height: 32,
                                borderRadius: 1,
                                bgcolor: 'rgba(255,201,101,0.2)',
                                color: GOLD,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                            <AccountBalanceWalletIcon sx={{ fontSize: 18 }} />
                        </Box>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 11,
                                letterSpacing: '0.14em',
                                textTransform: 'uppercase',
                                color: ON_VARIANT,
                            }}>
                            Available Balance
                        </Typography>
                    </Box>
                    {balance?.withdrawable ? (
                        <Box
                            sx={{
                                px: 1,
                                py: 0.25,
                                borderRadius: 999,
                                bgcolor: 'rgba(0,253,193,0.1)',
                                color: MINT,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 10,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                            }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: MINT }} />
                            Ready
                        </Box>
                    ) : null}
                </Box>
                <Box sx={{ zIndex: 1 }}>
                    {loading ? (
                        <Skeleton variant="text" width={160} height={48} sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontWeight: 900,
                                    fontSize: { xs: 32, sm: 36 },
                                    color: GOLD,
                                    letterSpacing: '-0.03em',
                                }}>
                                {balance ? money.format(balance.balance) : '—'}
                            </Typography>
                            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 12, color: ON_VARIANT }}>
                                {balance?.currency || 'USD'}
                            </Typography>
                        </Box>
                    )}
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 11, color: ON_VARIANT, mt: 0.5 }}>
                        Ready for payout on your campaign balance
                    </Typography>
                </Box>
                {scheduledLabel ? (
                    <Box
                        sx={{
                            p: 1.5,
                            borderRadius: 1.5,
                            bgcolor: SURFACE,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            zIndex: 1,
                        }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: ON_VARIANT }}>
                            <EventIcon sx={{ fontSize: 16, color: GOLD }} />
                            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 12 }}>
                                Scheduled Auto Payout
                            </Typography>
                        </Box>
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 12, color: '#fff' }}>
                            {scheduledLabel}
                        </Typography>
                    </Box>
                ) : null}
                <Button
                    fullWidth
                    onClick={onCashOut}
                    startIcon={<PaymentsIcon />}
                    sx={{
                        py: 1.5,
                        borderRadius: 999,
                        border: `2px solid ${GOLD}`,
                        color: GOLD,
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontWeight: 800,
                        fontSize: 14,
                        boxShadow: '0 0 16px rgba(255,201,101,0.15)',
                        zIndex: 1,
                        '&:hover': { bgcolor: GOLD, color: '#392700' },
                    }}>
                    Cash Out
                </Button>
            </Box>

            <Box
                sx={{
                    p: 3,
                    borderRadius: 1.5,
                    bgcolor: SURFACE_LOW,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 12,
                        letterSpacing: '0.14em',
                        textTransform: 'uppercase',
                        color: '#fff',
                    }}>
                    Earnings Summary
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                    <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: SURFACE }}>
                        <Typography
                            sx={{
                                fontSize: 10,
                                letterSpacing: '0.08em',
                                textTransform: 'uppercase',
                                color: ON_VARIANT,
                            }}>
                            Total Earned
                        </Typography>
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 18, color: '#fff', mt: 0.5 }}>
                            {totalEarned}
                        </Typography>
                        {monthlyEarned != null ? (
                            <Typography sx={{ fontSize: 10, color: MINT, fontFamily: FONT_ENUM.MAIN_BOLD, mt: 0.5 }}>
                                {fromCents(monthlyEarned)} this month
                            </Typography>
                        ) : null}
                    </Box>
                    <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: SURFACE }}>
                        <Typography
                            sx={{
                                fontSize: 10,
                                letterSpacing: '0.08em',
                                textTransform: 'uppercase',
                                color: ON_VARIANT,
                            }}>
                            Affiliates
                        </Typography>
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 18, color: GOLD, mt: 0.5 }}>
                            {fromCents(affiliates)}
                        </Typography>
                        <Typography sx={{ fontSize: 10, color: ON_VARIANT, mt: 0.5 }}>Cleared commission</Typography>
                    </Box>
                </Box>
                {goalPct != null && !Number.isNaN(goalPct) ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 12, color: '#fff' }}>
                                Monthly Goal
                            </Typography>
                            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 12, color: MINT }}>
                                {Math.round(goalPct)}%{monthlyTarget ? ` of ${fromCents(monthlyTarget)}` : ''}
                            </Typography>
                        </Box>
                        <LinearProgress
                            variant="determinate"
                            value={Math.min(100, goalPct)}
                            sx={{
                                height: 8,
                                borderRadius: 999,
                                bgcolor: SURFACE_HI,
                                '& .MuiLinearProgress-bar': {
                                    borderRadius: 999,
                                    bgcolor: MINT,
                                    boxShadow: '0 0 8px #00fdc1',
                                },
                            }}
                        />
                        {remaining != null ? (
                            <Typography sx={{ fontSize: 11, color: ON_VARIANT }}>
                                {fromCents(remaining)} from your monthly target.
                            </Typography>
                        ) : null}
                    </Box>
                ) : null}
            </Box>

            <Box
                sx={{
                    p: 2.5,
                    borderRadius: 1.5,
                    bgcolor: SURFACE_LOW,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                }}>
                {[
                    { label: 'Total Reach', value: reach, color: '#fff' },
                    { label: 'Avg Engagement', value: engagement.label, color: MINT },
                    {
                        label: 'Rating',
                        value: rating != null ? rating.toFixed(1) : '—',
                        color: GOLD,
                        star: rating != null,
                    },
                ].map((item, index) => (
                    <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                        {index > 0 ? <Box sx={{ width: '1px', height: 32, bgcolor: SURFACE_HI }} /> : null}
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    letterSpacing: '0.06em',
                                    textTransform: 'uppercase',
                                    color: ON_VARIANT,
                                }}>
                                {item.label}
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 16,
                                    color: item.color,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.25,
                                }}>
                                {item.value}
                                {item.star ? <StarIcon sx={{ fontSize: 14, color: GOLD }} /> : null}
                            </Typography>
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}
